//#region IMPORTS
import {AfterViewChecked, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {Router} from '@angular/router';
import {IFeature, ILoginStepProgress, INavigate, IUser, IUserAccess} from './features/user-access/models';
import {NavigationPageEnum} from './features/user-access/models/navigations.enums';
import {IPageObj, ISubPageObj} from './shared-utilities/models-old/page/page';
import {
  BARCODE_SCANNER_PAGES,
  DESKTOP_PAGES,
  MOBILE_PAGES,
  PAGES_SUB,
} from './shared-utilities/models-old/page/pages-const';
import {Store} from '@ngrx/store';
import {getDeepCopyOfObject, getShallowCopyOfObject} from './shared/shared-utils/object/object.utils';
import {
  selectIsUserNotificationsLoading,
  selectNotificationTotalsAggregated,
} from './features/notifications/store/notification.selectors';
import {
  selectIsUserAccessLoading,
  selectUserAccess,
} from './features/user-access/user-access-store/user-access.selectors';
import * as USER_SELECTORS from './features/user-access/user-store/user.selectors';
import {
  selectAllUserStoresOrdered,
  selectCurrentNavigation,
  selectSelectedFeature, selectSelectedStore,
  showLoadingIndicator
} from './features/user-access/user-stores-store/user-store.selectors';
import {take, takeUntil} from 'rxjs/operators';
import {LocalStorageService} from './features/core/service';
import {LocalStorageKeysEnum} from './features/core/models';
import {setAngularFireAuthPersistence, setPropertyOnUserState} from './features/user-access/user-store/user.actions';
import {setNavigation} from './features/user-access/user-stores-store/user-store.actions';
import {InactivityService} from './shared-utilities/services-old/inactivity.service';
import {INotificationTotals} from './features/notifications/models';
import {AuthPersistenceEnum, AuthPersistenceType} from './features/user-access/models/auth-persistence.enum';
import {IconsEnum} from './shared-modules/shared-icons/models/icons.enum';
import {scrollOnTouchForMobileDevice} from './shared/utils/mobile/mobile.utils';
import {DEFAULT_LOADER_INFINITE} from './shared/shared-module-ui/models/animations';
import {
  getDeviceOrientation,
  getDeviceOrientationAngle,
  getScreenOrientationAngle,
} from './features/user-access/utils/device.utils';
import {
  getUserDeviceOrientation,
  getUserDeviceOrientationAngles,
  getUserDevicePermissions,
  getUserDeviceUserAgent,
  getUserScreenOrientationAngles,
} from './features/user-access/user-device-store/user-device.actions';
import {selectIsDeviceMobile} from './features/user-access/user-device-store/user-device.selectors';
import {IStore} from './shared/shared-models/store/store';
import {FireAuthService} from './shared-utilities/services-old/fire-auth.service';
import {canSelectStoreOnCurrentPage, getAccessPageFromUrl} from './shared/guards/guard-utils';
import {DocumentTitleService} from './shared/shared-services/document-title/document-title.service';
import * as SharedActions from './features/core/store-shared/shared.actions';

//#endregion

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements AfterViewChecked, OnInit, OnDestroy {

  protected readonly icons: typeof IconsEnum = IconsEnum;
  protected readonly animation = DEFAULT_LOADER_INFINITE;

  private currentNavigationPage: INavigate = {currentPage: 'login', currentTab: null};
  private loginStepProgress: ILoginStepProgress = undefined;
  private user: IUser;
  private userAccess: IUserAccess | null = null;
  private selectedStore: IStore | null = null;
  private destroy$ = new Subject<boolean>();
  private destroyLoginSteps$ = new Subject<boolean>();
  private showModals$: Observable<boolean>;
  protected showLoadingIndicator$: Observable<boolean>;
  private isLogOutModalOpen$: Observable<boolean>;
  private isUserAccessLoading$: Observable<boolean>;
  private isUserNotificationsLoading$: Observable<boolean>;
  private notificationTotals$: Observable<INotificationTotals>;
  private userAccess$: Observable<IUserAccess>;
  protected userStores$: Observable<IStore[]>;
  private allMenuPages = DESKTOP_PAGES;
  selectedFeature$: Observable<IFeature>;

  constructor(
    private cdr: ChangeDetectorRef,
    private inactivityService: InactivityService,
    private readonly localStorageService: LocalStorageService,
    private readonly store: Store,
    private router: Router,
    private readonly fireAuth: FireAuthService,
    private readonly documentTitleService: DocumentTitleService,
  ) {
    this.inactivityService.startWatching();
  }

  //#region GET ACCESSORS
  get getCopyOfMainPages(): IPageObj[] {
    return [...this.allMenuPages];
  }

  get getCopyOfSubPages(): ISubPageObj {
    return getDeepCopyOfObject(PAGES_SUB);
  }

  get getCurrentSelectedNavigationPage(): INavigate {
    return this.currentNavigationPage;
  }

  get getIsCurrentUserAccessLoading$(): Observable<boolean> {
    return this.isUserAccessLoading$;
  }

  get getIsCurrentSelectedNavigationPageLogin(): boolean {
    return this.currentNavigationPage?.currentPage === NavigationPageEnum.Login;
  }

  get getIsLogOutModalOpen$(): Observable<boolean> {
    return this.isLogOutModalOpen$;
  }

  get getIsUserNotificationsLoading$(): Observable<boolean> {
    return this.isUserNotificationsLoading$;
  }

  get getNotificationTotals$(): Observable<INotificationTotals> {
    return this.notificationTotals$;
  }

  get getUser(): IUser {
    return this.user;
  }

  get getUserAccess$(): Observable<IUserAccess> {
    return this.userAccess$;
  }

  get getUserLoginProgressPercentileEqual100(): boolean {
    return this.loginStepProgress?.percentComplete === 100;
  }

  get showFeatureStoreNoAccess(): boolean {
    const page: string = getAccessPageFromUrl(this.router.url) || this.currentNavigationPage?.currentPage || '';
    return !canSelectStoreOnCurrentPage(
      this.fireAuth,
      this.selectedStore?.storeId,
      page,
    );
  }

  get featureStoreNoAccessStoreName(): string {
    return this.selectedStore?.name?.trim() ?? '';
  }

  get showGlobalModal$(): Observable<boolean> {
    return this.showModals$;
  }

  //#endregion

  @HostListener('document:touchstart', ['$event'])
  onDocumentTouch(event: Event): void {
    this.handleDocumentTouchStartInteraction(event);
  }

  ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    this.handleDeviceOrientationChanges();
    this.handleDeviceUserAgent();
    scrollOnTouchForMobileDevice(navigator, <AddEventListenerOptions>{once: true});

    this.getAuthPersistence();
    this.handleLoadUserLastLoginTimestamp();
    this.isLogOutModalOpen$ = this.store.select(USER_SELECTORS.selectIsUserLogOutModalOpen);
    this.isUserAccessLoading$ = this.store.select(selectIsUserAccessLoading);
    this.selectedFeature$ = this.store.select(selectSelectedFeature);
    this.isUserNotificationsLoading$ = this.store.select(selectIsUserNotificationsLoading);
    this.notificationTotals$ = this.store.select(selectNotificationTotalsAggregated);
    this.showModals$ = this.store.select(USER_SELECTORS.selectIsGlobalModalOpen);
    this.showLoadingIndicator$ = this.store.select(showLoadingIndicator);
    this.userAccess$ = this.store.select(selectUserAccess);
    this.userStores$ = this.store.select(selectAllUserStoresOrdered);

    this.store.select(selectUserAccess)
      .pipe(takeUntil(this.destroy$))
      .subscribe((access: IUserAccess): void => {
        this.userAccess = access && Object.keys(access).length > 0 ? access : null;
        this.cdr.markForCheck();
      });

    this.store.select(selectSelectedStore)
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedStore: IStore): void => {
        this.selectedStore = selectedStore ?? null;
        this.cdr.markForCheck();
      });

    this.store.select(selectCurrentNavigation)
      .pipe(takeUntil(this.destroy$))
      .subscribe((navigation: INavigate): void => {
        if (navigation?.currentPage) {
          this.currentNavigationPage = navigation;
          this.cdr.markForCheck();
        }
      });

    this.store.select(USER_SELECTORS.selectIsUserLoggedIn)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoggedIn: boolean): void => {
        const currentUrl = this.router.url;
        const urlIncludesAuth = currentUrl.startsWith('/auth') || window.location.pathname.startsWith('/auth');
        if (isLoggedIn === false && !urlIncludesAuth) {
          this.currentNavigationPage = {currentPage: NavigationPageEnum.Login, currentTab: null};
          this.store.dispatch(setNavigation({
            feature: NavigationPageEnum.Login,
            navigation: {currentPage: NavigationPageEnum.Login, currentTab: null},
          }));
          this.cdr.markForCheck();
        }
      });

    this.store.select(USER_SELECTORS.selectUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: IUser): void => {
        if (user) {
          this.user = user;
          this.cdr.markForCheck();
        }
      });

    this.store.select(USER_SELECTORS.selectLoginProgressProperty)
      .pipe(takeUntil(this.destroyLoginSteps$))
      .subscribe((loginSteps: ILoginStepProgress): void => {
        this.loginStepProgress = getShallowCopyOfObject(loginSteps);
        if (this.loginStepProgress?.percentComplete === 100) {
          this.destroyLoginSteps$.next(true);
          this.destroyLoginSteps$.complete();
        }
        this.cdr.markForCheck();
      });

    this.store.select(selectIsDeviceMobile)
      .pipe(take(1))
      .subscribe((isDeviceMobile: boolean): void => {
        this.allMenuPages = [];
        this.allMenuPages = isDeviceMobile === true ? [...MOBILE_PAGES, ...BARCODE_SCANNER_PAGES] : DESKTOP_PAGES;
      });

    this.store.dispatch(SharedActions.checkVersionOnAppLoad());

  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.destroyLoginSteps$.next(true);
    this.destroyLoginSteps$.complete();
  }

  getCurrentPageText(): string {
    const page = this.getCurrentSelectedNavigationPage?.currentPage;
    const tab = this.getCurrentSelectedNavigationPage?.currentTab;
    const allSubPages = this.getCopyOfSubPages;

    if (page === NavigationPageEnum.AutoOrdering && tab === 'schedule') {
      return 'Ordering Schedule';
    }

    if (window.innerWidth < 600 && page === NavigationPageEnum.StockManager) {
      return 'Stock Lookup';
    }

    const menuPages: IPageObj[] = window.innerWidth < 600
      ? this.getCopyOfMainPages
      : DESKTOP_PAGES;
    const mainPage: IPageObj | undefined = menuPages
      .find((menuPage: IPageObj): boolean => menuPage.page === page);

    const subPage = (tab && allSubPages[page])
      ? allSubPages[page].find((sp: IPageObj): boolean => sp.tab === tab)
      : undefined;

    if (subPage && subPage.text !== mainPage?.text) return mainPage ? `${mainPage.text} - ${subPage.text}` : '';
    return mainPage ? mainPage.text : '';
  }

  onMenuSizeChange(value: string): void {
    const sideNav = document.querySelector('.side-nav');
    if (!sideNav) return;
    value === 'expand'
      ? sideNav.classList.add('expanded-nav')
      : sideNav.classList.remove('expanded-nav');
  }

  onToggleSideMenu(value: 'contract' | 'expand'): void {
    const sideNav = document.querySelector('.side-nav');
    if (!sideNav) return;
    value === 'expand'
      ? sideNav.classList.add('expanded-nav')
      : sideNav.classList.remove('expanded-nav');
  }

  private getAuthPersistence(): void {
    const localPersistence = this.localStorageService
      .getLocalStorageItem<{ persistence: AuthPersistenceType }>(LocalStorageKeysEnum.authPersistence);
    this.store.dispatch(
      setAngularFireAuthPersistence({
        persistence: localPersistence ? AuthPersistenceEnum[localPersistence.persistence] : AuthPersistenceEnum.session,
      }),
    );
  }

  private handleDeviceOrientationChanges(): void {
    this.store.dispatch(getUserDeviceOrientation({orientation: getDeviceOrientation()}));
    this.store.dispatch(getUserScreenOrientationAngles({angle: getScreenOrientationAngle()}));

    window.screen.orientation?.addEventListener('change', (): void => {
      this.store.dispatch(getUserDeviceOrientation({orientation: getDeviceOrientation()}));
      this.store.dispatch(getUserScreenOrientationAngles({angle: getScreenOrientationAngle()}));
    });

    window.addEventListener('deviceorientation', (event: DeviceOrientationEvent): void => {
      this.store.dispatch(getUserDeviceOrientation({orientation: getDeviceOrientation()}));
      this.store.dispatch(getUserDeviceOrientationAngles({angle: getDeviceOrientationAngle(event)}));
      this.store.dispatch(getUserScreenOrientationAngles({angle: getScreenOrientationAngle()}));
    });
  }

  private handleDeviceUserAgent(): void {
    this.store.dispatch(getUserDeviceUserAgent());
    this.store.dispatch(getUserDevicePermissions());
  }

  private handleDocumentTouchStartInteraction(event: Event): void {
    const sideNav = document.querySelector('.side-nav');
    const toggleButton = document.querySelector('.toggle-button');
    if (sideNav?.classList.contains('expanded-nav')) {
      if (sideNav.contains(event.target as Node) || toggleButton?.contains(event.target as Node)) return;
      sideNav.classList.remove('expanded-nav');
    }
  }

  private handleLoadUserLastLoginTimestamp(): void {
    const lastLogin = this.localStorageService.getLocalStorageItem<{ date: string }>('authLastLogin');
    if (lastLogin?.date) {
      this.store.dispatch(setPropertyOnUserState({property: 'lastLogin', value: lastLogin.date}));
    }
  }

}
